import { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type { Amc, AmcAssignmentSummary } from "features/Amc/types/amc.types";

function assigneeLabel(a: AmcAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: AmcAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dedupeKey(a: AmcAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function AmcAssigneesAvatarGroup({
  contract,
  workAssignments,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  contract: Amc;
  workAssignments?: AmcAssignmentSummary[] | null;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (contract: Amc) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = (workAssignments ?? []).filter((a) => !a.deleted_at);
    const map = new Map<number, AmcAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => ({
      key: dedupeKey(a) || a.id,
      userId: assigneeUserId(a),
      label: assigneeLabel(a),
      avatar: a.user?.avatar,
      employee: a.user?.employee
        ? { id: a.user.employee.id, code: a.user.employee.code ?? "" }
        : undefined,
    }));
  }, [workAssignments]);

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={`/amc-management/${contract.id}`}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(contract)}
      emptyTitle="No assignees — open record to assign"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
